/**
 * Common API response and request types
 */

// Generic API response wrapper
export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  message?: string;
}

// Success/Error response pattern
export interface ApiSuccessResponse<T = unknown> {
  success: true;
  data: T;
  message?: string;
}

export interface ApiErrorResponse {
  success: false;
  error: string;
  detail?: unknown;
  errors?: Record<string, string[]>;
  status?: number;
}

export type ApiResult<T = unknown> = ApiSuccessResponse<T> | ApiErrorResponse;

// Paginated response pattern
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
}

// List response pattern
export interface ListResponse<T> {
  items: T[];
  count: number;
}

// Common request patterns
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface FilterParams {
  search?: string;
  filters?: Record<string, unknown>;
  dateFrom?: string;
  dateTo?: string;
}

export interface BulkOperationRequest<T = string> {
  ids: T[];
  operation: string;
  params?: Record<string, unknown>;
}

export interface BulkOperationResponse {
  successful: string[];
  failed: Array<{
    id: string;
    error: string;
  }>;
  total: number;
}

// API Error class
export class ApiError extends Error {
  public status?: number;
  public detail?: string;
  public errors?: Record<string, string[]>;
  public requestId?: string;
  public code?: string;
  public retryAfterSeconds?: number;

  constructor(
    message: string,
    status?: number,
    detail?: string,
    errors?: Record<string, string[]>,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.errors = errors;
    this.requestId = requestId;
  }

  static fromResponse(response: ApiErrorResponse): ApiError {
    return new ApiError(
      response.error,
      response.status,
      typeof response.detail === 'string' ? response.detail : undefined,
      response.errors
    );
  }
}

// Type guards
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isApiErrorResponse(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response &&
    response.success === false
  );
}

export function isApiSuccessResponse<T>(response: ApiResult<T>): response is ApiSuccessResponse<T> {
  return response.success === true;
}
